<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useTimerStore } from '@/stores/timer';
import { computeCountdown } from '@/util/timer-display';

const router = useRouter();
const timer = useTimerStore();

let tickHandle: number | undefined;

onMounted(async () => {
  // Make sure the store knows whether there's an active timer; once
  // it does, the FAB pops in. Cheap (single GET) and ignored when no
  // session is active.
  try { await timer.fetchActive(); } catch { /* ignore */ }
  tickHandle = window.setInterval(async () => {
    timer.bumpTick();
    // Off-page safety net: if the user navigated away from the event
    // detail, TimerBlock isn't mounted to fire the auto-stop. Do it
    // here. The store's mutex ensures we never double-stop with
    // TimerBlock if it IS mounted.
    await timer.maybeAutoStop();
  }, 1000);
});
onUnmounted(() => { if (tickHandle) window.clearInterval(tickHandle); });

const visible = computed(() => !!timer.active);

const display = computed(() =>
  computeCountdown(timer.elapsedSeconds, timer.active?.plannedDurationSeconds ?? null),
);

const isPaused = computed(() => timer.active?.status === 'paused');
const iconName = computed(() => {
  if (isPaused.value) return 'mdi-pause';
  return display.value.overrun ? 'mdi-fire' : 'mdi-timer-play-outline';
});

function open() {
  if (timer.active) router.push(`/event/${timer.active.eventId}`);
}
</script>

<template>
  <button
    v-if="visible"
    class="timer-fab"
    :class="{
      'timer-fab--paused': isPaused,
      'timer-fab--overrun': display.overrun && !isPaused,
    }"
    :aria-label="isPaused ? 'Gepauzeerde timer — open event' : 'Lopende timer — open event'"
    @click="open"
  >
    <v-icon :icon="iconName" color="white" size="22" />
    <span class="timer-fab__time numeric">{{ display.label }}</span>
  </button>
</template>

<style scoped>
.timer-fab {
  position: fixed;
  /* Bottom-left so it never collides with the + FAB on Schedule
     (which lives at right:16px). */
  left: 16px;
  bottom: calc(72px + env(safe-area-inset-bottom));
  z-index: 1004;

  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px 0 12px;
  height: 56px;
  border-radius: 28px;
  border: none;
  background: var(--lockin-primary, #1FA8F2);
  color: white;
  font: inherit;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);

  /* Subtle pulse so a running timer reads as "live". */
  animation: timer-fab-pulse 2.4s ease-in-out infinite;
}
.timer-fab--paused {
  background: #6B7280;     /* Vuetify grey-darken-1 */
  animation: none;
}
.timer-fab--overrun {
  background: #F59E0B;     /* warning — matches the overrun colour in TimerBlock */
  animation: timer-fab-pulse-overrun 1.6s ease-in-out infinite;
}
@keyframes timer-fab-pulse-overrun {
  0%, 100% { box-shadow: 0 4px 14px rgba(245, 158, 11, 0.5); }
  50%      { box-shadow: 0 4px 24px rgba(245, 158, 11, 0.85); }
}
.timer-fab__time {
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 0.02em;
}
@keyframes timer-fab-pulse {
  0%, 100% { box-shadow: 0 4px 14px rgba(31, 168, 242, 0.45); }
  50%      { box-shadow: 0 4px 22px rgba(31, 168, 242, 0.75); }
}
</style>
